import { useState } from 'react';
import { auth } from '@/lib/firebase';
import { useIndexShortcuts } from '@/hooks/useIndexShortcuts';
import IndexMark from './IndexMark';
import IndexSidebar, { IndexMobileMenuButton, type IndexNavKey } from './IndexSidebar';
import IndexStatusFooter from './IndexStatusFooter';

type IndexWorkspaceShellProps = {
  children: React.ReactNode;
  activeNav?: IndexNavKey;
};

function workspaceSlug(): string {
  const user = auth.currentUser;
  if (!user?.email) return 'WORKSPACE';
  const local = user.email.split('@')[0] ?? 'user';
  return local.replace(/[^a-zA-Z0-9]/g, '-').toUpperCase().slice(0, 24);
}

export default function IndexWorkspaceShell({ children, activeNav }: IndexWorkspaceShellProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  useIndexShortcuts();
  const user = auth.currentUser;
  const now = new Date();
  const dateStr = now.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
  const timeStr = now.toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });

  return (
    <div className="min-h-screen flex bg-black ni-grid-bg text-white">
      <IndexSidebar
        activeNav={activeNav}
        mobileOpen={mobileOpen}
        onMobileClose={() => setMobileOpen(false)}
      />
      <div className="flex flex-1 flex-col min-w-0 min-h-screen">
        <header className="sticky top-0 z-40 border-b border-[#2a2a2a] bg-black/95 backdrop-blur-sm">
          <div className="flex items-center justify-between gap-4 px-4 py-3">
            <div className="flex items-center gap-3 min-w-0">
              <IndexMobileMenuButton onClick={() => setMobileOpen(true)} />
              <div className="lg:hidden">
                <IndexMark compact />
              </div>
              <p className="hidden sm:block ni-id truncate text-white/45">
                NUCLIUS://INDEX/WORKSPACE/{workspaceSlug()}
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0 text-right">
              <div className="hidden md:block">
                <p className="text-[10px] font-mono uppercase tracking-wider text-white/40">
                  {dateStr} · {timeStr}
                </p>
                <p className="text-xs font-sans text-white/55 truncate max-w-[200px]">
                  {user?.email ?? '—'}
                </p>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 overflow-auto ni-scroll px-4 py-6 md:px-6 md:py-8">
          {children}
        </main>
        <IndexStatusFooter />
      </div>
    </div>
  );
}
